import { gql, gqlFetch } from '@/service/gql'
import { getCustomer } from '@/customer/customer'
import { openCookieConsentModal } from '@/messaging/systemModals'
import { isMarketplaceEU } from '@/customer/isMarketplaceEU'
import { getAuth } from '@/auth/authRef'
import { AuthStatus } from '@/auth/auth'
import type { Unsubscribe } from '@amazon/vinyl-util'

// language=GraphQL
const getCookieConsentQuery = gql(`
  query GetCookieConsent {
    userPreferences {
      cookieConsent
    }
  }
`)

// language=GraphQL
const updateCookieConsentMutation = gql(`
  mutation UpdateCookieConsent($consentStatus: CookieConsentInput) {
    updateCookieConsent(consentStatus: $consentStatus) {
      cookieConsent
    }
  }
`)

async function runCookieConsentCheck(): Promise<void> {
    const { customerHome } = await getCustomer()
    const territory = customerHome.musicTerritory

    if (!isMarketplaceEU(territory)) return
    if (territory === 'IN') return

    const result = await gqlFetch(getCookieConsentQuery, {})
    if (result.userPreferences?.cookieConsent !== 'NOT_SET') return

    openCookieConsentModal({
        onAccept: () => {
            void gqlFetch(updateCookieConsentMutation, { consentStatus: 'ACCEPT_ALL' })
        },
        onDecline: () => {
            void gqlFetch(updateCookieConsentMutation, { consentStatus: 'REJECT_ALL' })
        },
    })
}

/**
 * Wait until the customer is signed in, then check whether the cookie
 * consent modal needs to be shown. Returns a disposer that cancels the
 * subscription. If sign-in never happens, the check never runs.
 */
export function checkAndShowCookieConsentModal(): Unsubscribe {
    let ran = false
    const auth = getAuth()

    const trigger = () => {
        if (ran) return
        ran = true
        runCookieConsentCheck().catch(() => undefined)
    }

    if (auth.isSignedIn()) {
        trigger()
        return () => undefined
    }

    return auth.status.onData((status) => {
        if (status === AuthStatus.SIGNED_IN) trigger()
    })
}
